// 引入 React 运行时。
import React from 'react';
// 引入国际化 Hook。
import { useTranslations } from 'next-intl';
// 引入消息数据类型。
import { INotificationItem } from '@/api/message';
// 引入日期格式化工具。
import { formatDateByDayJs } from '@/utils/formatDateByDayJs';
// 引入通用图标组件。
import CommonIcon from '@/components/CommonIcon';
// 引入页面路径常量。
import { pagePathUrl } from '@/constants/url';
// 引入 HTML 实体解码工具。
import { decodeHtmlEntities } from '@/utils/html';
// 引入骨架屏组件。
import Skeleton from 'react-loading-skeleton';
// 引入骨架屏默认样式。
import 'react-loading-skeleton/dist/skeleton.css';
// 引入当前组件样式。
import styles from './index.module.scss';

// 定义组件入参类型。
interface MessageListProps {
  // 消息列表数据。
  data: INotificationItem[];
  // 加载状态。
  loading?: boolean;
  // 积分兑换弹层打开回调。
  onOpenPointsExchange?: () => void;
}

// 消息列表组件定义。
const MessageList: React.FC<MessageListProps> = ({ data, loading, onOpenPointsExchange }) => {
  // 获取国际化函数。
  const t = useTranslations();

  // 构造标题展示文本。
  const getTitle = (item: INotificationItem) => {
    // 对标题进行实体解码后返回。
    return decodeHtmlEntities(item.parent_type || t('user_center_message_notice'));
  };

  // 构造摘要展示文本。
  const getSummary = (item: INotificationItem) => {
    // 点赞类消息直接使用 i18n 模板。
    if (item.type === 'like' || item.type === 'praise') {
      // 返回点赞文案。
      return t('user_center_message_like_post_summary', { author: item.author || '' });
    }
    // 回复/评论消息先去标签再解码。
    if (item.type === 'reply' || item.type === 'comment') {
      // 返回纯文本摘要。
      return decodeHtmlEntities((item.note || '').replace(/<[^>]+>/g, ''));
    }
    // 其他消息同样按纯文本处理。
    return decodeHtmlEntities((item.note || '').replace(/<[^>]+>/g, ''));
  };

  // 构造右侧动作按钮文案。
  const getActionText = (item: INotificationItem) => {
    // 积分通知展示“查看积分”。
    if (item.parent_type === '积分兑换审核通知') return t('user_center_message_view_points');
    // 互动消息展示“查看详情”。
    if (item.parent_type === '互动消息') return t('user_center_message_view_detail');
    // 默认无动作文案。
    return '';
  };

  // 处理摘要可读化与长度截断。
  const getDisplaySummary = (summary: string) => {
    // 归一化 a 标签 href，确保站内链接带 / 前缀。
    let normalizedSummary = summary.replace(/<a href="([^"]+)"/g, (match, href) => {
      // 已有前缀直接返回原匹配。
      if (href.startsWith('/')) return match;
      // 补全前缀后返回。
      return `<a href="/${href}"`;
    });

    // 做实体解码并压缩空白字符。
    normalizedSummary = decodeHtmlEntities(normalizedSummary).replace(/\s+/g, ' ').trim();
    // 限制摘要最大长度。
    const maxLength = 128;
    // 未超长直接返回。
    if (normalizedSummary.length <= maxLength) return normalizedSummary;
    // 超长则截断并追加省略号。
    return `${normalizedSummary.slice(0, maxLength).trimEnd()}...`;
  };

  // 构造系统通知 HTML 摘要。
  const getSystemSummaryHtml = (item: INotificationItem) => {
    // 处理 a 标签 href 并附加蓝色样式。
    return (item.note || '').replace(/<a href="([^"]+)"/g, (match, href) => {
      // 已是相对路径时直接返回。
      if (href.startsWith('/')) return match;
      // 补齐相对路径并加样式。
      return `<a href="/${href}" style='color: #0060EF;'`;
    }).replace(/&nbsp;/g, ' ');
  };

  // 处理右侧动作点击行为。
  const handleActionClick = (item: INotificationItem) => {
    // 积分通知点击打开积分兑换弹层。
    if (item.parent_type === '积分兑换审核通知') {
      // 触发外部回调。
      onOpenPointsExchange?.();
      // 结束当前流程。
      return;
    }
    // 兜底取 tid 或 from_id。
    const tid = item.tid || item.from_id;
    // 获取评论偏移参数。
    const offset = item.offset;
    // 无 tid 则不跳转。
    if (!tid) return;
    // 打开详情页并携带 offset。
    window.open(`${pagePathUrl.detail}?tid=${tid}&offset=${offset}`, '_blank');
  };

  // 非加载且无数据时展示空态。
  if (!loading && data.length === 0) {
    // 返回空态节点。
    return (
      // 空态容器。
      <div className={styles.empty}>
        {/* 空态文案 */}
        {t('user_center_pc_no_messages')}
      </div>
    );
  }

  // 首次加载且无数据时展示骨架屏。
  if (loading && data.length === 0) {
    // 返回骨架列表。
    return (
      // 列表容器。
      <div className={styles.list}>
        {/* 渲染固定数量骨架项 */}
        {Array.from({ length: 4 }).map((_, index) => (
          // 骨架项容器。
          <div key={`message-skeleton-${index}`} className={styles.messageSkeletonItem}>
            {/* 骨架头部 */}
            <div className={styles.messageSkeletonHeader}>
              {/* 标题骨架 */}
              <Skeleton width={180} height={22} />
              {/* 日期骨架 */}
              <Skeleton width={90} height={16} />
            </div>
            {/* 摘要骨架 */}
            <div className={styles.messageSkeletonSummary}>
              {/* 摘要多行骨架 */}
              <Skeleton count={3} height={16} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // 常规渲染消息列表。
  return (
    // 列表容器。
    <div className={styles.list}>
      {/* 遍历消息项 */}
      {data.map((item) => {
        // 计算原始摘要。
        const rawSummary = getSummary(item);
        // 计算动作文案。
        const actionText = getActionText(item);
        // 计算展示摘要。
        const summary = getDisplaySummary(rawSummary);
        // 判断是否系统通知。
        const isSystemNotice = item.type === 'system';
        // 返回单条消息项。
        return (
          // 单条项容器。
          <div key={item.id} className={styles.item}>
            {/* 消息头部 */}
            <div className={styles.header}>
              {/* 标题 */}
              <span className={styles.title}>{getTitle(item)}</span>
              {/* 日期 */}
              <span className={styles.date}>{formatDateByDayJs(item.dateline)}</span>
            </div>
            {/* 摘要区域 */}
            <div className={styles.summary} title={rawSummary}>
              {/* 系统通知使用 HTML 渲染，其他消息渲染纯文本 */}
              {isSystemNotice ? (
                // 系统通知摘要。
                <span
                  // 摘要文本样式。
                  className={styles.summaryText}
                  // 注入处理后的 HTML。
                  dangerouslySetInnerHTML={{ __html: getSystemSummaryHtml(item) }}
                />
              ) : (
                // 普通摘要文本。
                <span className={styles.summaryText}>{summary}</span>
              )}
              {/* 存在动作文案时展示动作按钮 */}
              {!!actionText && (
                // 行内动作按钮。
                <div
                  // 样式类名。
                  className={styles.inlineAction}
                  // 点击处理。
                  onClick={() => handleActionClick(item)}
                >
                  {/* 动作文案 */}
                  <span>{actionText}</span>
                  {/* 右箭头图标 */}
                  <CommonIcon icon="go-to-detail" className={styles.actionArrow} />
                </div>
              )}
            </div>
          </div>
        );
      })}
      {/* 分页加载中状态 */}
      {loading && (
        // 分页 loading 容器。
        <div className={styles.paginationLoading}>
          {/* loading 点 */}
          <span className={styles.loadingDot} />
          {/* loading 文案 */}
          <span>{t('user_center_loading')}</span>
        </div>
      )}
    </div>
  );
};

// 导出组件。
export default MessageList;
